import { createForm } from "@tanstack/solid-form";
import type { Component } from "solid-js";
import { toast } from "solid-sonner";
import { useLayoutContext } from "@/components/layout/context/LayoutContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  FieldContainer,
  FieldContainerLabel,
  FieldInfo,
  Input,
} from "@/components/ui/input";
import { useAuthV2 } from "@/domains/auth/context/auth-provider";
import { useSendSupportRequest } from "../api";
import { supportFormSchema } from "../validators/support";

export const SupportModal: Component = () => {
  const { mutateAsync: sendSupportRequest, isPending } =
    useSendSupportRequest();

  const { isSupportModalOpen, setIsSupportModalOpen } = useLayoutContext();

  const [authStoreV2] = useAuthV2();

  const form = createForm(() => ({
    defaultValues: {
      email: authStoreV2?.profile?.email ?? "",
      message: "",
    },
    onSubmit: async ({ value }) => {
      const validatedData = supportFormSchema.safeParse(value);
      if (!validatedData.success) {
        console.error("Validation error", validatedData.error);
        return;
      }

      try {
        await sendSupportRequest(validatedData.data);
        toast.success("Richiesta di supporto inviata con successo!");
        setIsSupportModalOpen(false);
        form.reset();
      } catch {
        toast.error("Errore nell'invio della richiesta");
      }
    },
  }));

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      form.reset();
    }
    setIsSupportModalOpen(open);
  };

  return (
    <Dialog open={isSupportModalOpen()} onOpenChange={setIsSupportModalOpen}>
      <DialogContent class="sm:max-w-md">
        <DialogHeader>
          <DialogTitle class="text-lg font-semibold text-gray-900">
            Richiesta di Supporto
          </DialogTitle>
          <DialogDescription>
            Hai bisogno di aiuto? Compila il form e ti risponderemo il prima
            possibile.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          class="space-y-4"
        >
          <form.Field
            name="email"
            validators={{
              onChange: supportFormSchema.shape.email,
            }}
          >
            {(field) => (
              <FieldContainer>
                <FieldContainerLabel for="email">Email</FieldContainerLabel>
                <Input
                  id={field().name}
                  name={field().name}
                  type="email"
                  value={field().state.value ?? ""}
                  onInput={(e) => field().handleChange(e.target.value)}
                  onBlur={() => field().handleBlur()}
                  onChange={(e) => field().handleChange(e.target.value)}
                  placeholder="tua-email@esempio.it"
                />
                <FieldInfo field={field()} />
              </FieldContainer>
            )}
          </form.Field>

          <form.Field
            name="message"
            validators={{
              onChange: supportFormSchema.shape.message,
            }}
          >
            {(field) => (
              <FieldContainer>
                <FieldContainerLabel for="message">
                  Messaggio
                </FieldContainerLabel>
                <textarea
                  id={field().name}
                  name={field().name}
                  value={field().state.value ?? ""}
                  onInput={(e) => field().handleChange(e.currentTarget.value)}
                  onBlur={() => field().handleBlur()}
                  class="flex min-h-[120px] w-full rounded-md border border-input bg-card px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                  placeholder="Descrivi il tuo problema o la tua richiesta..."
                  rows={5}
                />
                <div class="flex justify-between items-center gap-2">
                  <FieldInfo field={field()} />
                  <span class="text-xs text-gray-500 ml-auto">
                    {field().state.value.length}/500
                  </span>
                </div>
              </FieldContainer>
            )}
          </form.Field>

          <div class="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              Annulla
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? "Invio in corso..." : "Invia Richiesta"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
